/**
 * ProceduralBackground — renders a seeded, coordinate-based procedural background
 * that pans and zooms coherently with the spatial canvas camera.
 *
 * Rendering is driven IMPERATIVELY via a ref handle (`redraw(pan, zoom)`) so the
 * background stays in perfect lockstep with the world-layer CSS transform — both
 * are updated in the same animation frame by SpatialCanvasLayout. This avoids the
 * drift you get when the background only redraws on React re-render.
 *
 * The component owns the canvas backing-store size (DPR-aware); renderers must NOT
 * resize the canvas, only paint into it.
 *
 * Debug: ?debug_bg=1  or  localStorage.debug_bg = '1'
 * Exposes: window.__procBg  (generator, seed, renderer, lastDrawMs)
 */

import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { SpaceBackground } from '../../../types/spaces';
import { getGenerator } from './proceduralRegistry';
import type { ProceduralRenderer, DrawArgs } from './ProceduralRenderer';

function isDebug() {
  if (typeof window === 'undefined') return false;
  return (
    localStorage.getItem('debug_bg') === '1' ||
    new URLSearchParams(window.location.search).get('debug_bg') === '1'
  );
}

async function loadRenderer(generatorId: string): Promise<new () => ProceduralRenderer> {
  if (generatorId === 'topo') return (await import('./webgl/topoContour')).TopoContourWebGL;
  if (generatorId === 'riverNetwork') return (await import('./webgl/riverNetwork')).RiverNetworkWebGL;
  if (generatorId === 'haze') return (await import('./webgl/haze')).HazeWebGL;
  if (generatorId === 'contours') return (await import('./webgl/contours')).ContoursWebGL;
  if (generatorId === 'dotGrid') return (await import('./webgl/dotGrid')).DotGridWebGL;
  if (generatorId === 'cuttingMat') return (await import('./webgl/cuttingMat')).CuttingMatWebGL;
  if (generatorId === 'starfield')  return (await import('./webgl/starfield')).StarfieldWebGL;
  if (generatorId === 'arabesque')  return (await import('./webgl/arabesque')).ArabesqueWebGL;
  if (generatorId === 'domeV2')     return (await import('./webgl/domeTile')).DomeTileRenderer;
  throw new Error(`[BG_PROC] Unknown generator: ${generatorId}`);
}

export interface ProceduralBackgroundHandle {
  /** Redraw immediately at the given camera. Called from the layout's RAF loop. */
  redraw: (pan: { x: number; y: number }, zoom: number) => void;
}

interface ProceduralBackgroundProps {
  background: SpaceBackground;
  /** Initial camera; subsequent updates come through the imperative redraw handle. */
  initialPan: { x: number; y: number };
  initialZoom: number;
}

const ProceduralBackground = forwardRef<ProceduralBackgroundHandle, ProceduralBackgroundProps>(
  ({ background, initialPan, initialZoom }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rendererRef = useRef<ProceduralRenderer | null>(null);
    const readyRef = useRef(false);

    const panRef = useRef(initialPan);
    const zoomRef = useRef(initialZoom);

    const generatorId = background.generator ?? 'topo';
    const seed = background.seed ?? 42;
    const params = background.params ?? {};

    const gen = getGenerator(generatorId);
    const palette = gen?.palettes.find(p => p.id === (background.palette ?? gen.defaultPalette));
    const paletteColors = palette?.colors ?? ['#e8f4e8', '#c5dba4', '#9dc87a', '#4a7a3a', '#2d5226'];

    // The actual paint. Owns canvas sizing (DPR). Renderers only paint.
    const paint = (pan: { x: number; y: number }, zoom: number) => {
      const renderer = rendererRef.current;
      const canvas = canvasRef.current;
      if (!renderer || !canvas || !readyRef.current) return;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const bw = Math.max(1, Math.round(w * dpr));
      const bh = Math.max(1, Math.round(h * dpr));
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }

      const args: DrawArgs = { pan, zoom, width: w, height: h, dpr, seed, params, paletteColors };

      const t0 = performance.now();
      renderer.draw(args);
      const ms = performance.now() - t0;

      if (isDebug()) {
        (window as any).__procBg = {
          ...(window as any).__procBg,
          generator: generatorId,
          seed,
          renderer: 'webgl',
          lastDrawMs: Math.round(ms * 10) / 10,
        };
      }
    };

    useImperativeHandle(ref, () => ({
      redraw: (pan, zoom) => {
        panRef.current = pan;
        zoomRef.current = zoom;
        paint(pan, zoom);
      },
    }));

    // Mount / remount renderer when the generator changes. The canvas is keyed
    // by generatorId so a FRESH DOM element is created each time.
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      let disposed = false;
      readyRef.current = false;

      loadRenderer(generatorId)
        .then(RendererClass => {
          if (disposed) return;
          const renderer = new RendererClass();
          try {
            renderer.mount(canvas);
          } catch (err) {
            console.error('[BG_PROC] mount failed:', err);
            return;
          }
          rendererRef.current = renderer;
          readyRef.current = true;
          if (isDebug()) console.log('[BG_PROC] Mounted renderer:', generatorId);
          paint(panRef.current, zoomRef.current);
        })
        .catch(err => console.error('[BG_PROC] Failed to load renderer:', err));

      return () => {
        disposed = true;
        readyRef.current = false;
        rendererRef.current?.dispose();
        rendererRef.current = null;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generatorId]);

    // Repaint when seed / palette / params change (not camera — that's imperative).
    useEffect(() => {
      if (readyRef.current) paint(panRef.current, zoomRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seed, background.palette, JSON.stringify(params)]);

    return (
      <canvas
        key={generatorId}
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
          // Fill with the palette's base tone while the WebGL shader compiles
          // (~50–200 ms on first use) so the underlying layout doesn't flash
          // through during mount.
          backgroundColor: paletteColors[0],
        }}
      />
    );
  }
);

ProceduralBackground.displayName = 'ProceduralBackground';

export default ProceduralBackground;
